import { useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';

import {
  SET_ON_CONTINUE,
  SET_LOADING,
  SET_SUCCESS,
} from '../../reducers/actionReducer';
import LoadingSpinner from '../LoadingSpinner';
import ErrorText from '../ErrorText';
import MultiSelectInput from '../inputs/MultiSelectInput';

const EDIT_USER_ROLES = gql`
  mutation EditUserRoles($id: ID!, $roles: [String]!) {
    editUserRoles(id: $id, roles: $roles) {
      message
    }
  }
`;

type EditUserRolesProps = {
  id: string;
  dispatchAction: (action: any) => void;
  usersQuery: any;
  row: any;
};

export default function EditUserRoles({
  id,
  dispatchAction,
  usersQuery,
  row,
}: EditUserRolesProps) {
  const [editUserRoles, { loading, error, called, data }] = useMutation(
    EDIT_USER_ROLES,
    {
      refetchQueries: [usersQuery],
    }
  );

  const [roles, setRoles] = useState<{ value: string; label: string }[]>(() => {
    const roles = row.original.roles;

    if (roles) {
      return roles
        .filter((role) => role !== 'user') // Remove legacy 'user' role for display
        .map((role) => {
          return { value: role, label: role };
        });
    }

    return [];
  });

  useEffect(() => {
    if (id && editUserRoles) {
      dispatchAction({
        type: SET_ON_CONTINUE,
        payload: {
          onContinue: () =>
            editUserRoles({
              variables: { id, roles: roles.map(({ value }) => value) },
            }),
        },
      });
    }
  }, [dispatchAction, id, editUserRoles, roles]);

  useEffect(() => {
    if (loading !== undefined) {
      dispatchAction({
        type: SET_LOADING,
        payload: { loading },
      });
    }
  }, [dispatchAction, loading]);

  useEffect(() => {
    if (called && data && !error) {
      dispatchAction({
        type: SET_SUCCESS,
        payload: {
          message: 'User roles edited successfully.',
        },
      });
    }
  }, [called, data, dispatchAction, error]);

  return (
    <div className="flex flex-col justify-center items center text-center space-y-4">
      {loading && <LoadingSpinner />}
      {error && <ErrorText message={error.message} />}
      {!loading && !error && (
        <>
          <p>Select or deselect the roles you want to assign to this user.</p>
          <div className="px-5">
            {/*
              Option list must stay in sync with the server allow-list in
              `server/apollo/resolvers/user/mutations.js#editUserRoles` and
              the enum in `server/db/models/user.model.js`. `activations-user`
              is a capability add-on (gates the Activations module) — it can
              be combined with any user tier. So is `model-files-user`
              (ATAD-146: importing from the Alliant data sources), which also needs a tier
              that can upload files.
            */}
            <MultiSelectInput
              value={roles}
              options={[
                { value: 'view-only', label: 'View Only (Demo/Intern)' },
                { value: 'standard-user', label: 'Standard User (Marketing/Sales)' },
                { value: 'super-user', label: 'Super User (Consulting/Sales Engineering)' },
                { value: 'super-user-pii', label: 'Super User w/PII (Small Business Owner)' },
                { value: 'admin', label: 'Admin' },
                { value: 'finance-admin', label: 'Finance Admin' },
                { value: 'activations-user', label: 'Activations User (LiveRamp Distribution)' },
                { value: 'model-files-user', label: 'Alliant Data Sources (CMDA, First Party, Syndicated)' },
              ]}
              onChange={(newValue) => {
                setRoles(newValue);
              }}
              noOptionsMessage="No roles."
            />
          </div>
        </>
      )}
    </div>
  );
}
